import { apiClient } from '@/shared/lib/apiClient';
import type {
  ProductResponseDTO,
  ProductListQuery,
  ProductListResponse,
  CreateProductDTO,
  UpdateProductDTO,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export async function listProducts(query?: ProductListQuery): Promise<ProductListResponse> {
  const params: Record<string, string> = {};
  if (query?.cursor) params['cursor'] = query.cursor;
  if (query?.limit !== undefined) params['limit'] = String(query.limit);
  if (query?.search) params['search'] = query.search;
  if (query?.is_active !== undefined) params['is_active'] = String(query.is_active);

  const res = await apiClient.get<ApiResponse<ProductListResponse>>('/api/v1/products', {
    params,
  });
  return res.data;
}

export async function getProduct(id: string): Promise<ProductResponseDTO> {
  const res = await apiClient.get<ApiResponse<ProductResponseDTO>>(`/api/v1/products/${id}`);
  return res.data;
}

export async function getProductBySlug(slug: string): Promise<ProductResponseDTO> {
  const res = await apiClient.get<ApiResponse<ProductResponseDTO>>(`/api/v1/products/slug/${slug}`);
  return res.data;
}

export async function createProduct(data: CreateProductDTO): Promise<ProductResponseDTO> {
  const res = await apiClient.post<ApiResponse<ProductResponseDTO>>('/api/v1/products', data);
  return res.data;
}

export async function updateProduct(
  id: string,
  data: UpdateProductDTO,
): Promise<ProductResponseDTO> {
  const res = await apiClient.patch<ApiResponse<ProductResponseDTO>>(
    `/api/v1/products/${id}`,
    data,
  );
  return res.data;
}

export async function activateProduct(id: string): Promise<ProductResponseDTO> {
  const res = await apiClient.post<ApiResponse<ProductResponseDTO>>(
    `/api/v1/products/${id}/activate`,
    {},
  );
  return res.data;
}

export async function deactivateProduct(id: string, force?: boolean): Promise<ProductResponseDTO> {
  const res = await apiClient.post<ApiResponse<ProductResponseDTO>>(
    `/api/v1/products/${id}/deactivate`,
    { force },
  );
  return res.data;
}

export async function deleteProduct(id: string): Promise<void> {
  await apiClient.delete<ApiResponse<void>>(`/api/v1/products/${id}`);
}

export async function rotateRedirectSecret(
  id: string,
  newSecret: string,
): Promise<ProductResponseDTO> {
  const res = await apiClient.post<ApiResponse<ProductResponseDTO>>(
    `/api/v1/products/${id}/rotate-secret`,
    { new_secret: newSecret },
  );
  return res.data;
}

export async function rotateApiKey(id: string): Promise<ProductResponseDTO> {
  const res = await apiClient.post<ApiResponse<ProductResponseDTO>>(
    `/api/v1/products/${id}/rotate-api-key`,
    {},
  );
  return res.data;
}
